import { LedgerEntry, Ledger, LedgerTransaction, LedgerInvite } from '../types/ledger';

export const CURRENT_USER_ID = 'user-1'; // Same as other modules

// Mock ledger invites
export const mockLedgerInvites: LedgerInvite[] = [
  {
    id: 'invite-1',
    ledgerId: 'ledger-acc-pending-1',
    ledgerName: 'Office Supplies Account',
    fromUserId: 'user-5',
    fromUserName: 'Vikram Singh',
    toUserId: CURRENT_USER_ID,
    status: 'pending',
    createdAt: new Date('2024-01-16'),
  },
];

// Mock ledger accounts
export const mockLedgers: Ledger[] = [
  {
    id: 'ledger-acc-1',
    userId: CURRENT_USER_ID,
    personName: 'Rajesh Kumar',
    balance: 15000, // They owe you
    totalReceived: 25000,
    totalPaid: 10000,
    lastTransactionDate: new Date('2024-01-15'),
    isBookmarked: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-15'),
    isShared: true,
    linkedUserId: 'user-2',
    linkStatus: 'accepted',
    createdBy: CURRENT_USER_ID,
    isViewOnly: false,
  },
  {
    id: 'ledger-acc-2',
    userId: CURRENT_USER_ID,
    personName: 'Priya Sharma - Rent',
    balance: -8000, // You owe them
    totalReceived: 5000,
    totalPaid: 13000,
    lastTransactionDate: new Date('2024-01-12'),
    isBookmarked: false,
    createdAt: new Date('2024-01-02'),
    updatedAt: new Date('2024-01-12'),
    isShared: true,
    linkedUserId: 'user-3',
    linkStatus: 'accepted',
    createdBy: 'user-3',
    isViewOnly: true,
  },
  {
    id: 'ledger-acc-3',
    userId: CURRENT_USER_ID,
    personName: 'Amit Patel',
    balance: 25000,
    totalReceived: 35000,
    totalPaid: 10000,
    lastTransactionDate: new Date('2024-01-10'),
    isBookmarked: true,
    createdAt: new Date('2024-01-03'),
    updatedAt: new Date('2024-01-10'),
    isShared: false,
    linkStatus: 'none',
    createdBy: CURRENT_USER_ID,
    isViewOnly: false,
  },
  {
    id: 'ledger-acc-4',
    userId: CURRENT_USER_ID,
    personName: 'Sunita Verma',
    balance: -12000,
    totalReceived: 8000,
    totalPaid: 20000,
    lastTransactionDate: new Date('2024-01-08'),
    isBookmarked: false,
    createdAt: new Date('2024-01-04'),
    updatedAt: new Date('2024-01-08'),
    isShared: false,
    linkStatus: 'none',
    createdBy: CURRENT_USER_ID,
    isViewOnly: false,
  },
  {
    id: 'ledger-acc-5',
    userId: CURRENT_USER_ID,
    personName: 'Vikram Singh',
    balance: 8000,
    totalReceived: 18000,
    totalPaid: 10000,
    lastTransactionDate: new Date('2024-01-05'),
    isBookmarked: false,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-05'),
  },
  {
    id: 'ledger-acc-6',
    userId: CURRENT_USER_ID,
    personName: 'Anjali Desai',
    balance: 0,
    totalReceived: 15000,
    totalPaid: 15000,
    lastTransactionDate: new Date('2024-01-03'),
    isBookmarked: false,
    createdAt: new Date('2024-01-06'),
    updatedAt: new Date('2024-01-03'),
  },
];

// Mock ledger entries
export const mockLedgerEntries: LedgerEntry[] = [
  {
    id: 'ledger-1',
    userId: CURRENT_USER_ID,
    personName: 'Rajesh Kumar',
    type: 'received',
    amount: 15000,
    note: 'Website development - Phase 1',
    date: new Date('2024-01-15'),
    createdAt: new Date('2024-01-15'),
  },
  {
    id: 'ledger-2',
    userId: CURRENT_USER_ID,
    personName: 'Priya Sharma',
    type: 'paid',
    amount: 5000,
    note: 'Office rent - January',
    date: new Date('2024-01-10'),
    createdAt: new Date('2024-01-10'),
  },
  {
    id: 'ledger-3',
    userId: CURRENT_USER_ID,
    personName: 'Amit Patel',
    type: 'received',
    amount: 25000,
    note: 'Mobile app project advance',
    date: new Date('2024-01-08'),
    createdAt: new Date('2024-01-08'),
  },
  {
    id: 'ledger-4',
    userId: CURRENT_USER_ID,
    personName: 'Sunita Verma',
    type: 'paid',
    amount: 3500,
    note: 'Marketing services - December',
    date: new Date('2024-01-05'),
    createdAt: new Date('2024-01-05'),
  },
  {
    id: 'ledger-5',
    userId: CURRENT_USER_ID,
    personName: 'Vikram Singh',
    type: 'received',
    amount: 8000,
    note: 'Consulting fees',
    date: new Date('2024-01-03'),
    createdAt: new Date('2024-01-03'),
  },
];

// Initialize mock data in storage (for development/demo purposes)
export const initializeMockLedgerData = async () => {
  const { storage } = await import('../utils/storage');
  try {
    const existingEntries = await storage.getLedgerEntries();
    // Only initialize if storage is empty
    if (existingEntries.length === 0) {
      await storage.saveLedgerEntries(mockLedgerEntries);
      console.log('Mock ledger data initialized');
    }
  } catch (error) {
    console.error('Error initializing mock ledger data:', error);
  }
};

// Utility function to get relative time
export const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
};

// Format date for display
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  return dateObj.toLocaleDateString('en-IN', options);
};

// Get entries by filter
export const getEntriesByFilter = (
  entries: LedgerEntry[],
  filter: 'all' | 'received' | 'paid'
): LedgerEntry[] => {
  if (filter === 'all') {
    return entries;
  }
  return entries.filter(entry => entry.type === filter);
};

// Sort entries by date (latest first)
export const sortEntriesByDate = (entries: LedgerEntry[]): LedgerEntry[] => {
  return [...entries].sort((a, b) => b.date.getTime() - a.date.getTime());
};

// Get entries for current user
export const getUserEntries = (userId: string): LedgerEntry[] => {
  return mockLedgerEntries.filter(entry => entry.userId === userId);
};
